import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, IconButton, Paper, Skeleton, Stack, Tab, Tabs, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { ButtonComponent, colors, FunctionsEnum } from '@watter/shared-components';
import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { CommonTable } from '../../components/common-styles';
import { HeaterLight } from '../../components/icons/heater-light';
import { GET_CUSTOMER_HEATER_METRICS, GET_DEVICE_STATUS_HISTORY, GET_TEMPARATURE_HISTORY } from '../../graphql/queries';
import { client } from '../../service/apolloClient';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { formatDateWithTimeWithSeconds, formatToUSDate } from '../../utils/helper';
import { MainWrapper } from '../common-styles';
import { AddHeaterWrapper, CustomTabs, EditWrapper } from './manage-customer-styles';
import HeaterNamePopup from './update-heater';
import { RootState } from '../../store';
import { openToast } from '../../store/reducers/toastSlice';
import { usePermissions } from '../../custom_hooks/permissions';

interface TabPanelProps {
    children: React.ReactNode;
    index: number;
    value: number;
}

const CustomTabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;

    return (
        <Box
            role="tabpanel"
            // height="calc(100dvh - 180px)"
            overflow="auto"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
            sx={{
                '& .cstTabHeader': {
                    position: 'absolute',
                    top: '14px',
                    left: 0,
                },
            }}
        >
            {value === index && <Box>{children}</Box>}
        </Box>
    );
};

const CustomNoTemperatureRowsOverlay = (): ReactElement => {
    const { t } = useTranslation();
    const activeHTTPRequests = useSelector((state: RootState) => state?.app?.activeHTTPRequests);
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'gray',
            }}
        >
            <Typography>{activeHTTPRequests > 0 ? t('data_fetching') : t('temperature_data_unavailable')}</Typography>
        </Box>
    );
};
const CustomNoConnectivityRowsOverlay = (): ReactElement => {
    const { t } = useTranslation();
    const activeHTTPRequests = useSelector((state: RootState) => state?.app?.activeHTTPRequests);
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'gray',
            }}
        >
            <Typography>{activeHTTPRequests > 0 ? t('data_fetching') : t('connectivity_data_unavailable')}</Typography>
        </Box>
    );
};
const HeaterMetrics = ({ userData, onEditEvent }: any) => {
    const { t } = useTranslation();
    const [value, setValue] = React.useState(0);
    const [customerHeaterDetails, setCustomerHeaterDetails] = useState<any>();
    const [deviceTempHistory, setDeviceTempHistory] = useState<any>([]);
    const [deviceStatusHistory, setDeviceStatusHistory] = useState<any>([]);
    const [showUpdateHeaterModal, setShowUpdateHeaterModal] = useState<boolean>(false);
    const [showUpdateHeaterModalHeading, setShowUpdateHeaterModalHeading] = useState<string>(t('edit_heater_id'));
    const dispatch = useDispatch();
    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };
    const columns: GridColDef[] = [
        {
            field: 'date',
            headerName: 'Date',
            flex: 1,
        },
        { field: 'temp_avg', headerName: 'Water Temparature', flex: 1 },
    ];
    const connectivityColumns: GridColDef[] = [
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            renderCell: params => {
                return (
                    <Stack flexDirection="row" alignItems="center" display="flex" height="100%">
                        <Typography variant="subtitle2" sx={{ fontSize: '14px' }}>
                            {t('status')}
                        </Typography>
                        <Typography
                            variant="subtitle2"
                            pl={1}
                            sx={{
                                fontWeight: 'bold',
                                color: params?.value === 0 ? '#EF4444' : '#22C55E',
                                fontSize: '14px',
                            }}
                        >
                            {params?.value === 0 ? t('disconnected') : t('connected')}
                        </Typography>
                    </Stack>
                );
            },
        },
        { field: 'date', headerName: 'Date & Time', flex: 1 },
    ];

    function a11yProps(index: number) {
        return {
            id: `simple-tab-${index}`,
            'aria-controls': `simple-tabpanel-${index}`,
        };
    }

    const getCustomerDetails = React.useCallback(async () => {
        try {
            dispatch(showLoader());
            const { data } = await client.query({
                query: GET_CUSTOMER_HEATER_METRICS,
                variables: { userId: userData?.id },
                fetchPolicy: 'network-only',
            });

            if (data?.getCustomerDeviceMetrics) {
                setCustomerHeaterDetails(data?.getCustomerDeviceMetrics);
            }
        } catch (err) {
            console.error('Error fetching customer details:', err);
            dispatch(
                openToast({
                    show: true,
                    message: t('unable_load_heater_info_msg'),
                    severity: 'error',
                    isAlertOpen: true,
                }),
            );
        } finally {
            dispatch(hideLoader());
        }
    }, [dispatch, userData?.id, t]);
    useEffect(() => {
        getCustomerDetails();
    }, [userData?.id, getCustomerDetails]);
    const getTemparatureHistory = React.useCallback(async () => {
        if (!customerHeaterDetails?.device_id) return;
        try {
            dispatch(showLoader());
            const { data } = await client.query({
                query: GET_TEMPARATURE_HISTORY,
                variables: { deviceId: customerHeaterDetails?.device_id },
                fetchPolicy: 'network-only',
            });
            if (data?.getTemperatureHistory) {
                const formattedRows = data.getTemperatureHistory.history.map(
                    (row: { date: any; temp_avg: any }, index: any) => ({
                        id: index,
                        date: row.date,
                        temp_avg: `${row?.temp_avg}°F`,
                    }),
                );
                setDeviceTempHistory(formattedRows);
            }
        } catch (err) {
            console.error('Error fetching customer details:', err);
        } finally {
            dispatch(hideLoader());
        }
    }, [dispatch, customerHeaterDetails?.device_id]);
    const getDeviceHistory = React.useCallback(async () => {
        try {
            dispatch(showLoader());

            const { data } = await client.query({
                query: GET_DEVICE_STATUS_HISTORY,
                variables: { deviceId: customerHeaterDetails?.device_id },
                fetchPolicy: 'network-only',
            });

            if (data?.getCustomerDeviceStatusHistory) {
                const formattedRows = data.getCustomerDeviceStatusHistory.map(
                    (row: { createdAt: any; status: any; sensor_timestamp: any }, index: any) => ({
                        id: index,
                        date: formatDateWithTimeWithSeconds(row?.sensor_timestamp),
                        status: row?.status,
                    }),
                );
                setDeviceStatusHistory(formattedRows);
            }
        } catch (err) {
            console.error('Error fetching customer details:', err);
        } finally {
            dispatch(hideLoader());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, userData?.id, customerHeaterDetails?.device_id]);
    useEffect(() => {
        if (value === 0) {
            getTemparatureHistory();
        } else {
            getDeviceHistory();
        }
    }, [value, getTemparatureHistory, getDeviceHistory]);

    const handleEditClick = () => {
        const heading = customerHeaterDetails?.device_id ? t('edit_heater_id') : t('add_heater_id');
        setShowUpdateHeaterModalHeading(heading);
        setShowUpdateHeaterModal(true);
    };
    const handleCloseVerifyPopUp = () => {
        setShowUpdateHeaterModal(false);
    };

    const handleRefetchData = () => {
        getCustomerDetails();
        if (value === 0) {
            getTemparatureHistory();
        } else {
            getDeviceHistory();
        }
        onEditEvent();
    };
    const { canCreate, canEdit } = usePermissions();
    const renderHeaterMetricNoData = (): ReactElement => {
        return (
            <AddHeaterWrapper
                mt={1.5}
                sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                }}
            >
                <Stack
                    mb={2}
                    sx={{
                        height: '50vh', // Full viewport height
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        gap: 2,
                    }}
                >
                    <HeaterLight />
                    <Typography variant="subtitle1" color="info.100" sx={{ width: 300 }}>
                        {t('add_heater_msg')}
                    </Typography>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems="center"
                        justifyContent="space-between"
                        className="actionFooter"
                        spacing={2}
                        pt={2}
                    >
                        <Box className="cstBtnWrapper">
                            {canCreate(FunctionsEnum.MANAGE_CUSTOMERS) && (
                                <ButtonComponent
                                    size="medium"
                                    variant="contained"
                                    fullWidth
                                    buttonName={t('add')}
                                    className="cstBtn"
                                    onClick={handleEditClick}
                                />
                            )}
                        </Box>
                    </Stack>
                </Stack>
            </AddHeaterWrapper>
        );
    };

    const renderHeaterMetricDetail = (): ReactElement => {
        return (
            <>
                <EditWrapper mt={1.5}>
                    <Box className="listBox" mb={2}>
                        <Grid container flexGrow={1} className="details profileDetails" spacing={2}>
                            <Grid size={{ xs: 12, sm: 3 }}>
                                <Stack>
                                    <Typography variant="subtitle1" color="info.100">
                                        {t('heater_ID')}{' '}
                                    </Typography>
                                    <Stack flexDirection="row">
                                        <Typography variant="h5" py={1.5}>
                                            {customerHeaterDetails?.device_id || '--'}
                                        </Typography>
                                        {canEdit(FunctionsEnum.MANAGE_CUSTOMERS) &&
                                            customerHeaterDetails?.device_id && (
                                                <Box component="span" py={0.5} px={1}>
                                                    <IconButton onClick={handleEditClick} size="small">
                                                        <EditOutlinedIcon />
                                                    </IconButton>
                                                </Box>
                                            )}
                                    </Stack>
                                </Stack>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 3 }}>
                                <Stack>
                                    <Typography variant="subtitle1" color="info.100">
                                        {t('Installed_Date')}
                                    </Typography>
                                    <Typography variant="h5" py={1.5}>
                                        {customerHeaterDetails?.installed_date
                                            ? formatToUSDate(customerHeaterDetails?.installed_date)
                                            : '--'}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 3 }}>
                                <Stack className="cstVerify cstMobileNumber">
                                    <Typography variant="subtitle1" color="info.100">
                                        {t('Installed_By')}{' '}
                                    </Typography>
                                    <Typography variant="h5" py={1.5}>
                                        {customerHeaterDetails?.installed_by || '--'}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 3 }}>
                                <Stack className="cstEmail">
                                    <Typography variant="subtitle1" color="info.100">
                                        {t('power_status')}
                                    </Typography>
                                    <Typography variant="h5" py={1.5}>
                                        {customerHeaterDetails?.power ? 'On' : 'Off'}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 3 }}>
                                <Stack>
                                    <Typography variant="subtitle1" color="info.100">
                                        {t('Current_Power_usage')}{' '}
                                    </Typography>
                                    <Typography variant="h5" py={1.5}>
                                        {customerHeaterDetails?.power_usage
                                            ? `${customerHeaterDetails?.power_usage} W`
                                            : '--'}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 3 }}>
                                <Stack>
                                    <Typography variant="subtitle1" color="info.100">
                                        {t('Current_WATTER_Temperature')}{' '}
                                    </Typography>
                                    <Typography variant="h5" py={1.5}>
                                        {customerHeaterDetails?.water_temperature
                                            ? `${customerHeaterDetails?.water_temperature}°F`
                                            : '--'}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 3 }}>
                                <Stack>
                                    <Typography variant="subtitle1" color="info.100">
                                        {t('Current_Operating_Mode')}{' '}
                                    </Typography>
                                    <Typography variant="h5" py={1.5}>
                                        {customerHeaterDetails?.operation_mode || '--'}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 3 }}>
                                <Stack>
                                    <Typography variant="subtitle1" color="info.100">
                                        {t('Internet_Staus')}{' '}
                                    </Typography>
                                    <Typography variant="h5" py={1.5}>
                                        {customerHeaterDetails?.internet ? 'On' : 'Off'}
                                    </Typography>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Box>
                </EditWrapper>
                <Box position="relative">
                    <Stack direction="row" alignItems="center" justifyContent="flex-end" width="100%">
                        <CustomTabs>
                            <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
                                <Tab label={t('Temperature_Statistics')} {...a11yProps(0)} />
                                <Tab label={t('Connectivity_Status')} {...a11yProps(1)} />
                            </Tabs>
                        </CustomTabs>
                    </Stack>
                    <CustomTabPanel value={value} index={0}>
                        <Stack direction="row" alignItems="center" gap={1} className="cstTabHeader">
                            <Typography variant="h5" color="info.500" fontWeight={700} letterSpacing={0.5}>
                                {t('WATTER_Temperature_Statistics')}
                            </Typography>
                            <Typography
                                variant="subtitle1"
                                color="secondary"
                                fontWeight={500}
                                fontFamily="good_sansmedium"
                            >
                                {t('Last_Seven_Days')}
                            </Typography>
                        </Stack>
                        <CommonTable>
                            <Paper sx={{ width: '100%' }}>
                                <DataGrid
                                    rows={deviceTempHistory}
                                    columns={columns}
                                    disableColumnMenu
                                    disableColumnSorting
                                    hideFooter
                                    slots={{
                                        noRowsOverlay: CustomNoTemperatureRowsOverlay,
                                    }}
                                />
                            </Paper>
                        </CommonTable>
                    </CustomTabPanel>
                    <CustomTabPanel value={value} index={1}>
                        <Stack direction="row" alignItems="center" gap={1} className="cstTabHeader">
                            <Typography variant="h5" color={colors.lightHead} fontWeight={700} letterSpacing={0.5}>
                                {t('Connectivity_Status')}:
                            </Typography>
                            <Typography variant="subtitle1" fontFamily="good_sansmedium">
                                {t('Last_Seven_Days')}
                            </Typography>
                        </Stack>
                        <CommonTable>
                            <Paper sx={{ width: '100%' }}>
                                <DataGrid
                                    rows={deviceStatusHistory}
                                    columns={connectivityColumns}
                                    disableColumnMenu
                                    disableColumnSorting
                                    hideFooter
                                    slots={{
                                        noRowsOverlay: CustomNoConnectivityRowsOverlay,
                                    }}
                                />
                            </Paper>
                        </CommonTable>
                    </CustomTabPanel>
                </Box>
            </>
        );
    };

    if (!customerHeaterDetails)
        return (
            <MainWrapper
                mt={1.5}
                sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                }}
            >
                <Box
                    mb={2}
                    sx={{
                        height: '50vh', // Full viewport height
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        gap: 2,
                    }}
                >
                    <Skeleton />
                </Box>
            </MainWrapper>
        );
    return (
        <>
            {!customerHeaterDetails?.device_id ? renderHeaterMetricNoData() : renderHeaterMetricDetail()}
            <HeaterNamePopup
                open={showUpdateHeaterModal}
                closePopup={handleCloseVerifyPopUp}
                heading={showUpdateHeaterModalHeading}
                userId={userData?.id}
                refetchCustomerDetail={handleRefetchData}
                isEdit={customerHeaterDetails?.device_id !== ''}
                heaterIdValue={customerHeaterDetails?.device_id}
            />
        </>
    );
};
export default HeaterMetrics;
